import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";
import { Kbd } from "./kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

/** Compact 24/28px square icon control used across headers, toolbars and detail panes. */

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** 24px (`sm`) or 28px (`default`). */
  size?: "sm" | "default";
  /** Toggled-on appearance (view switchers, filter toggles). */
  active?: boolean;
  /** Required — becomes the accessible name and the tooltip body. */
  label: string;
  /** Set false to opt out of the automatic tooltip. */
  tooltip?: boolean;
  /** Keyboard hint rendered inside the tooltip. */
  shortcut?: string | string[];
  /** Render the styling onto the child element instead of a `<button>`. */
  asChild?: boolean;
  /** Tooltip placement. */
  tooltipSide?: "top" | "right" | "bottom" | "left";
}

const iconButtonSizes = {
  sm: "size-6 [&_svg]:size-3.5",
  default: "size-control [&_svg]:size-4",
} as const;

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      size = "default",
      active = false,
      label,
      tooltip = true,
      shortcut,
      asChild = false,
      tooltipSide = "bottom",
      className,
      type,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const control = (
      <Comp
        ref={ref}
        data-slot="icon-button"
        data-active={active ? "true" : undefined}
        type={asChild ? undefined : (type ?? "button")}
        aria-label={label}
        aria-pressed={active ? true : undefined}
        className={cn(
          "inline-grid shrink-0 place-items-center rounded-sm",
          "text-text-tertiary",
          "transition-[background-color,color] duration-100 ease-out-fast",
          "hover:bg-hover hover:text-text active:bg-hover-strong",
          "focus-visible:outline-none focus-fill",
          "disabled:pointer-events-none disabled:opacity-50",
          "[&_svg]:pointer-events-none [&_svg]:shrink-0",
          iconButtonSizes[size],
          active && "bg-selected text-text",
          className,
        )}
        {...props}
      >
        {children}
      </Comp>
    );

    if (!tooltip) return control;

    // Self-contained provider: an `IconButton` must work anywhere, including
    // trees that have no app-level `TooltipProvider`. Nested providers are a
    // no-op beyond resetting the delay, which we pin to the system default.
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{control}</TooltipTrigger>
          <TooltipContent side={tooltipSide} className="flex items-center">
            <span>{label}</span>
            {shortcut ? (
              <Kbd keys={shortcut} size="sm" className="ml-2" />
            ) : null}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
IconButton.displayName = "IconButton";

export { IconButton };
